import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { fmtTRY, fmtGold, fmtDate } from "../lib/format";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Package, ScanLine, Truck, Coins, Bell, GemIcon, TrendingUp, ChevronLeft, RotateCcw, Plus,
} from "lucide-react";

const STATUS_STYLE = {
  rezerve:       "bg-amber-500/10 text-amber-400 ring-amber-500/30",
  hazirlandi:    "bg-sky-500/10 text-sky-400 ring-sky-500/30",
  teslim_edildi: "bg-emerald-500/10 text-emerald-400 ring-emerald-500/30",
};

const PRODUCTS = [
  { key: "gram",    label: "1 Gram",   grams_mg: 1000 },
  { key: "ceyrek",  label: "Çeyrek",   grams_mg: 1750 },
  { key: "yarim",   label: "Yarım",    grams_mg: 3500 },
  { key: "tam",     label: "Tam",      grams_mg: 7000 },
  { key: "bilezik", label: "Bilezik",  grams_mg: 15000 },
];

const seedOrders = () => {
  const now = Date.now();
  return [
    { id: "r1", event_title: "Ayşe & Mehmet",   display_name: "Zeynep A.", product_type: "ceyrek",  grams_mg: 1750,  locked_price_kurus: 280, lock_ref: "8HG3T4NB", status: "rezerve",       created_at: new Date(now - 6 * 60 * 1000).toISOString(),  starts_at: new Date(now + 3 * 3600 * 1000).toISOString() },
    { id: "r2", event_title: "Ayşe & Mehmet",   display_name: "Ali Y.",    product_type: "gram",    grams_mg: 1000,  locked_price_kurus: 280, lock_ref: "K2M9PLQR", status: "rezerve",       created_at: new Date(now - 22 * 60 * 1000).toISOString(), starts_at: new Date(now + 3 * 3600 * 1000).toISOString() },
    { id: "r3", event_title: "Nihal & Barış",    display_name: "Emre S.",   product_type: "yarim",   grams_mg: 3500,  locked_price_kurus: 279, lock_ref: "3F1W2XCM", status: "hazirlandi", barcode: "BAR-2091", delivery_code: "H7P2QK", created_at: new Date(now - 45 * 60 * 1000).toISOString(), prepared_at: new Date(now - 12 * 60 * 1000).toISOString(),  starts_at: new Date(now - 3600 * 1000).toISOString() },
    { id: "r4", event_title: "Selin Nişan",      display_name: "Onur T.",   product_type: "tam",     grams_mg: 7000,  locked_price_kurus: 281, lock_ref: "P9V0LM1A", status: "teslim_edildi", barcode: "BAR-2088", delivery_code: "K3B9Y2", created_at: new Date(now - 96 * 60 * 1000).toISOString(), prepared_at: new Date(now - 85 * 60 * 1000).toISOString(), delivered_at: new Date(now - 6 * 60 * 1000).toISOString(), starts_at: new Date(now - 5 * 3600 * 1000).toISOString() },
  ];
};

const seedStock = () => PRODUCTS.map(p => ({
  product_type: p.key, label: p.label, grams_mg: p.grams_mg,
  total_count: 8, reserved_count: p.key === "gram" ? 1 : p.key === "ceyrek" ? 1 : p.key === "yarim" ? 1 : 0,
  available_count: p.key === "gram" ? 7 : p.key === "ceyrek" ? 7 : p.key === "yarim" ? 7 : 8,
}));

const SAMPLE_NAMES = ["Zeynep A.", "Ali Y.", "Fatma D.", "Ayşegül B.", "Emre S.", "Selin M."];
const SAMPLE_EVENTS = ["Ayşe & Mehmet", "Nihal & Barış", "Elif Kına"];

export default function JewelerDemo() {
  const { t } = useI18n();
  const [orders, setOrders] = useState(() => seedOrders());
  const [stock, setStock]   = useState(() => seedStock());
  const [prices, setPrices] = useState({ gram_price_kurus: 260000, workmanship_bps: 800 });
  const [tab, setTab] = useState("orders");
  const [prepareVals, setPrepareVals] = useState({});
  const [deliverVals, setDeliverVals] = useState({});
  const audioRef = useRef(null);

  // Simulate a new incoming order every 25-40 seconds
  useEffect(() => {
    const iv = setInterval(() => {
      const p = PRODUCTS[Math.floor(Math.random() * 3)]; // gram/ceyrek/yarim (in-stock)
      const st = stock.find(s => s.product_type === p.key);
      if (!st || st.available_count <= 0) return;
      const price = Math.round((prices.gram_price_kurus / 1000) * (1 + prices.workmanship_bps / 10000));
      const newOrder = {
        id: `sim-${Date.now()}`,
        event_title: SAMPLE_EVENTS[Math.floor(Math.random() * SAMPLE_EVENTS.length)],
        display_name: SAMPLE_NAMES[Math.floor(Math.random() * SAMPLE_NAMES.length)],
        product_type: p.key, grams_mg: p.grams_mg,
        locked_price_kurus: price,
        lock_ref: Math.random().toString(36).slice(2, 10).toUpperCase(),
        status: "rezerve",
        created_at: new Date().toISOString(),
        starts_at: new Date(Date.now() + 2 * 3600 * 1000).toISOString(),
      };
      setOrders(o => [newOrder, ...o]);
      setStock(s => s.map(x => x.product_type === p.key ? { ...x, reserved_count: x.reserved_count + 1, available_count: Math.max(0, x.available_count - 1) } : x));
      try { audioRef.current?.play(); } catch { /* noop */ }
      toast.success("Yeni sipariş geldi", { description: `${p.label} · ${fmtGold(p.grams_mg)}` });
    }, 25000 + Math.floor(Math.random() * 15000));
    return () => clearInterval(iv);
  }, [stock, prices]);

  const summary = useMemo(() => {
    const rows = orders;
    const total = rows.length;
    const gr = rows.reduce((s, r) => s + (r.grams_mg || 0), 0);
    const total_kurus = rows.reduce((s, r) => s + Math.round((r.locked_price_kurus || 0) * (r.grams_mg || 0) / 1000), 0);
    const commission = Math.round(total_kurus * 500 / 10000);
    return { orders: total, grams_mg: gr, total_kurus, commission_kurus: commission, payable_kurus: total_kurus - commission };
  }, [orders]);

  const prepare = (id) => {
    const bar = (prepareVals[id] || "").trim();
    if (!bar) return toast.error("Barkod girin");
    const code = Math.random().toString(36).slice(2, 8).toUpperCase();
    setOrders(o => o.map(r => r.id === id ? { ...r, status: "hazirlandi", barcode: bar, delivery_code: code, prepared_at: new Date().toISOString() } : r));
    toast.success(`Hazırlandı — Teslim kodu: ${code}`);
  };
  const deliver = (id) => {
    const entered = (deliverVals[id] || "").trim().toUpperCase();
    const r = orders.find(x => x.id === id);
    if (!r) return;
    if (entered !== (r.delivery_code || "").toUpperCase()) return toast.error("Teslim kodu hatalı");
    setOrders(o => o.map(x => x.id === id ? { ...x, status: "teslim_edildi", delivered_at: new Date().toISOString() } : x));
    // Permanently consume reserved stock
    setStock(s => s.map(x => x.product_type === r.product_type ? { ...x, reserved_count: Math.max(0, x.reserved_count - 1), total_count: Math.max(0, x.total_count - 1) } : x));
    toast.success("Teslim edildi");
  };
  const stockDelta = (pt, d) => {
    setStock(s => s.map(x => {
      if (x.product_type !== pt) return x;
      const total = Math.max(0, x.total_count + d);
      const reserved = Math.min(x.reserved_count, total);
      return { ...x, total_count: total, reserved_count: reserved, available_count: Math.max(0, total - reserved) };
    }));
  };
  const savePrices = () => toast.success("Fiyat güncellendi (demo)");
  const reset = () => { setOrders(seedOrders()); setStock(seedStock()); toast.success("Demo sıfırlandı"); };

  return (
    <div className="relative">
      <div className="sticky top-16 z-30 bg-primary/95 text-primary-foreground text-xs uppercase tracking-[0.35em] py-1.5 text-center font-medium" data-testid="jeweler-demo-banner">
        <span className="inline-flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" />
          {t.demo_badge || "DEMO"} · Kuyumcu Paneli · {t.demo_note || "Her ziyaretçi kendi oturumunu görür"}
        </span>
      </div>

      <audio ref={audioRef} src="data:audio/wav;base64,UklGRlAAAABXQVZFZm10IBAAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=" preload="auto" />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-10">
        <div className="mb-6 flex items-start justify-between flex-wrap gap-3">
          <div>
            <Button asChild variant="ghost" size="sm" className="gap-1 -ms-2" data-testid="jeweler-demo-back">
              <Link to="/"><ChevronLeft className="w-4 h-4" /> Ana Sayfa</Link>
            </Button>
            <div className="text-xs uppercase tracking-[0.3em] text-primary/80 mt-2">{t.jeweler || "Kuyumcu"}</div>
            <h1 className="font-serif text-4xl gold-text">Altınbaş Kuyumcu · Nişantaşı</h1>
            <p className="text-sm text-muted-foreground">İstanbul · Komisyon 5% · Çalışma 10:00 - 19:00</p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40 max-w-xs">
              {t.marketplace_note}
            </div>
            <Button size="sm" variant="ghost" className="gap-1 rounded-full" onClick={reset} data-testid="jeweler-demo-reset">
              <RotateCcw className="w-3 h-3" /> Demo sıfırla
            </Button>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mb-6">
          {[
            [t.orders_today || "Bugünkü sipariş", summary.orders, Package],
            [t.grams_today || "Toplam gram",      fmtGold(summary.grams_mg), GemIcon],
            [t.total_cash || "Toplam nakit",      fmtTRY(summary.total_kurus), Coins],
            [t.commission || "Komisyon",           fmtTRY(summary.commission_kurus), TrendingUp],
            [t.payable    || "Ödenecek",           fmtTRY(summary.payable_kurus), TrendingUp],
          ].map(([label, val, Icon], i) => (
            <Card key={label} className="p-4" data-testid={`jd-stat-${i}`}>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-muted-foreground"><Icon className="w-3 h-3" /> {label}</div>
              <div className="font-mono text-xl mt-1">{val}</div>
            </Card>
          ))}
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 max-w-md">
            <TabsTrigger value="orders" data-testid="jd-tab-orders" className="gap-2"><Bell className="w-3 h-3" /> {t.orders || "Siparişler"}</TabsTrigger>
            <TabsTrigger value="stock" data-testid="jd-tab-stock">{t.stock || "Stok"}</TabsTrigger>
            <TabsTrigger value="prices" data-testid="jd-tab-prices">{t.prices || "Fiyatlar"}</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-5 space-y-3">
            {orders.map(r => (
              <Card key={r.id} className="p-5" data-testid={`jd-order-${r.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider text-primary/70">{r.product_type} · {fmtGold(r.grams_mg)}</div>
                    <div className="font-serif text-xl">{r.event_title}</div>
                    <div className="text-xs text-muted-foreground mt-1">{fmtDate(r.starts_at)} · {r.display_name}</div>
                  </div>
                  <div className="text-end">
                    <Badge className={`${STATUS_STYLE[r.status]} ring-1 uppercase tracking-wider text-[10px]`}>{t["status_" + r.status] || r.status}</Badge>
                    <div className="font-mono text-sm mt-2">{fmtTRY(Math.round(r.locked_price_kurus * r.grams_mg / 1000))}</div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.lock_ref || "Kilit No"}: {r.lock_ref}</div>
                  </div>
                </div>
                <div className="mt-4 grid sm:grid-cols-2 gap-3">
                  {r.status === "rezerve" && (
                    <div className="flex gap-2 items-end sm:col-span-2">
                      <div className="flex-1"><Label className="text-xs">{t.barcode || "Barkod"}</Label><Input value={prepareVals[r.id] || ""} onChange={(e) => setPrepareVals({ ...prepareVals, [r.id]: e.target.value })} placeholder="BAR-1234" data-testid={`jd-bar-${r.id}`} /></div>
                      <Button onClick={() => prepare(r.id)} className="rounded-full gap-2" data-testid={`jd-prepare-${r.id}`}><ScanLine className="w-4 h-4" /> {t.prepare || "Hazırla"}</Button>
                    </div>
                  )}
                  {r.status === "hazirlandi" && (
                    <>
                      <div className="p-3 rounded-lg bg-primary/10 ring-1 ring-primary/30 text-center">
                        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{t.delivery_code || "Teslim kodu"}</div>
                        <div className="font-mono text-xl gold-text">{r.delivery_code}</div>
                        <div className="text-[10px] text-muted-foreground mt-1">Çift bu kodu göstermeli</div>
                      </div>
                      <div className="flex gap-2 items-end">
                        <div className="flex-1"><Label className="text-xs">{t.scan_code || "Kodu okut"}</Label><Input value={deliverVals[r.id] || ""} onChange={(e) => setDeliverVals({ ...deliverVals, [r.id]: e.target.value })} placeholder="ABC123" data-testid={`jd-code-${r.id}`} /></div>
                        <Button onClick={() => deliver(r.id)} className="rounded-full gap-2" data-testid={`jd-deliver-${r.id}`}><Truck className="w-4 h-4" /> {t.deliver || "Teslim et"}</Button>
                      </div>
                    </>
                  )}
                  {r.status === "teslim_edildi" && (
                    <div className="text-xs text-emerald-400 sm:col-span-2">Teslim edildi: {fmtDate(r.delivered_at)} · Barkod: {r.barcode}</div>
                  )}
                </div>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="stock" className="mt-5">
            <Card className="p-5">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-widest text-muted-foreground">
                  <tr><th className="text-start py-2">Ürün</th><th>Gram (mg)</th><th>Toplam</th><th>Rezerve</th><th>Müsait</th><th>+/-</th></tr>
                </thead>
                <tbody>
                  {stock.map(s => (
                    <tr key={s.product_type} className="border-t" data-testid={`jd-stock-${s.product_type}`}>
                      <td className="py-3 font-serif">{s.label}</td>
                      <td className="text-center font-mono">{s.grams_mg}</td>
                      <td className="text-center font-mono">{s.total_count}</td>
                      <td className="text-center font-mono text-amber-400">{s.reserved_count}</td>
                      <td className="text-center font-mono text-emerald-400">{s.available_count}</td>
                      <td className="text-center">
                        <div className="inline-flex gap-1">
                          <Button size="sm" variant="outline" className="h-8 rounded-full" onClick={() => stockDelta(s.product_type, -1)} data-testid={`jd-minus-${s.product_type}`}>−1</Button>
                          <Button size="sm" className="h-8 rounded-full" onClick={() => stockDelta(s.product_type, +1)} data-testid={`jd-plus-${s.product_type}`}><Plus className="w-3 h-3" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </TabsContent>

          <TabsContent value="prices" className="mt-5">
            <Card className="p-6 max-w-md space-y-4">
              <div><Label>{t.gram_price || "Gram fiyatı (kuruş)"}</Label><Input type="number" value={prices.gram_price_kurus} onChange={(e) => setPrices({ ...prices, gram_price_kurus: parseInt(e.target.value || 0, 10) })} data-testid="jd-price-gram" /></div>
              <div><Label>{t.workmanship || "İşçilik (bps)"}</Label><Input type="number" value={prices.workmanship_bps} onChange={(e) => setPrices({ ...prices, workmanship_bps: parseInt(e.target.value || 0, 10) })} data-testid="jd-price-work" /></div>
              <Button onClick={savePrices} className="rounded-full" data-testid="jd-price-save">Kaydet</Button>
              <p className="text-xs text-muted-foreground">Girilen fiyat kilit anında 90 sn için baz alınır. Bu bir demodur.</p>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
